import { useEffect, useReducer, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  X,
  Search,
  Minus,
  Plus,
  Sparkles,
  RotateCcw,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  LayoutGrid,
  Layers,
  Package,
} from "lucide-react";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { listMenuItems } from "@/lib/api/admin";
import type { BundleDeal, MenuItem } from "@/lib/api/types";
import {
  validateRawSchedule,
  formToSchedule,
  scheduleToForm,
  type ScheduleFormState,
} from "@/lib/utils/schedule";
import { DealScheduleInput } from "@/components/schedule/DealScheduleInput";
import { SegmentedToggle } from "@/components/SegmentedToggle";
import { DiscardDialog } from "@/components/DiscardDialog";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatCents, parseCents } from "@/lib/utils/number";
import { cn } from "@/lib/utils";

// ─── Item row state ─────────────────────────────────────────────────────────────

type BundleItemRow = {
  menuItem: MenuItem;
  quantity: number;
};

const MAX_ITEM_ROWS = 10;
const MAX_QUANTITY = 20;

const PRESETS: { label: string; description: string; percent: number }[] = [
  { label: "10% Off", description: "Light nudge", percent: 10 },
  { label: "20% Off", description: "Popular pick", percent: 20 },
  { label: "25% Off", description: "Strong value", percent: 25 },
];

// ─── Form state ─────────────────────────────────────────────────────────────────

type BundleDealFormState = {
  name: string;
  bundlePrice: string; // dollar string, e.g. "18.00"
  maxClaimsPerCustomerPerDay: string;
  schedule: ScheduleFormState;
  status: "active" | "inactive";
};

type FormAction =
  | { type: "set"; field: "name" | "bundlePrice" | "maxClaimsPerCustomerPerDay"; value: string }
  | { type: "setStatus"; value: "active" | "inactive" }
  | { type: "setSchedule"; value: ScheduleFormState }
  | { type: "reset"; item?: BundleDeal };

function initForm(item?: BundleDeal): BundleDealFormState {
  if (!item) {
    return {
      name: "",
      bundlePrice: "",
      maxClaimsPerCustomerPerDay: "",
      schedule: scheduleToForm(),
      status: "active",
    };
  }
  return {
    name: item.name,
    bundlePrice: (item.bundle_price_cents / 100).toFixed(2),
    maxClaimsPerCustomerPerDay:
      item.max_claims_per_customer_per_day != null
        ? String(item.max_claims_per_customer_per_day)
        : "",
    schedule: scheduleToForm(item.schedule),
    status: item.status,
  };
}

function formReducer(state: BundleDealFormState, action: FormAction): BundleDealFormState {
  switch (action.type) {
    case "set":
      return { ...state, [action.field]: action.value };
    case "setStatus":
      return { ...state, status: action.value };
    case "setSchedule":
      return { ...state, schedule: action.value };
    case "reset":
      return initForm(action.item);
    default:
      return state;
  }
}

function initItems(item?: BundleDeal): BundleItemRow[] {
  if (!item) return [];
  return item.items.map((i) => ({ menuItem: i.menu_item, quantity: i.quantity }));
}

// ─── Bundle Deal Form Dialog ────────────────────────────────────────────────

type BundleDealFormDialogProps = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  storeId: string;
  item?: BundleDeal;
  onSave: (data: {
    name: string;
    bundle_price_cents: number;
    max_claims_per_customer_per_day: number | null;
    schedule: Record<string, { open: string; close: string }[]>;
    status: "active" | "inactive";
    items: { menu_item_id: string; quantity: number }[];
  }) => void;
  isPending: boolean;
  apiError?: string | null;
};

export function BundleDealFormDialog({
  open,
  onOpenChange,
  storeId,
  item,
  onSave,
  isPending,
  apiError,
}: BundleDealFormDialogProps) {
  const user = useAdminAuth();
  const isEditing = item != null;

  const [state, dispatch] = useReducer(formReducer, undefined, () => initForm(item));
  const [items, setItems] = useState<BundleItemRow[]>(() => initItems(item));
  const [dirty, setDirty] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const [nameError, setNameError] = useState<string | null>(null);
  const [priceError, setPriceError] = useState<string | null>(null);
  const [itemsError, setItemsError] = useState<string | null>(null);
  const [maxClaimsError, setMaxClaimsError] = useState<string | null>(null);
  const [scheduleErrors, setScheduleErrors] = useState<Record<string, string>>({});
  const [scheduleFormError, setScheduleFormError] = useState<string | null>(null);

  // Reset all local state whenever the dialog opens for a (possibly new) item.
  useEffect(() => {
    if (!open) return;
    dispatch({ type: "reset", item });
    setItems(initItems(item));
    setDirty(false);
    setPickerOpen(false);
    setHasAttemptedSubmit(false);
    setNameError(null);
    setPriceError(null);
    setItemsError(null);
    setMaxClaimsError(null);
    setScheduleErrors({});
    setScheduleFormError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id]);

  const busy = isPending;
  const atRowCap = items.length >= MAX_ITEM_ROWS;

  function addItem(mi: MenuItem) {
    setDirty(true);
    setItemsError(null);
    setItems((prev) => {
      const idx = prev.findIndex((r) => r.menuItem.id === mi.id);
      if (idx >= 0) {
        if (prev[idx]!.quantity >= MAX_QUANTITY) return prev;
        const next = [...prev];
        next[idx] = { ...next[idx]!, quantity: next[idx]!.quantity + 1 };
        return next;
      }
      if (prev.length >= MAX_ITEM_ROWS) return prev;
      return [...prev, { menuItem: mi, quantity: 1 }];
    });
  }

  function setQuantity(id: string, quantity: number) {
    setDirty(true);
    setItemsError(null);
    setItems((prev) =>
      prev.map((r) =>
        r.menuItem.id === id ? { ...r, quantity: Math.min(MAX_QUANTITY, Math.max(1, quantity)) } : r
      )
    );
  }

  function removeItem(id: string) {
    setDirty(true);
    setItems((prev) => prev.filter((r) => r.menuItem.id !== id));
  }

  // ── Live preview figures — computed client-side since there's no preview endpoint. ──
  const regularPriceCents = items.reduce((sum, r) => sum + r.menuItem.price_cents * r.quantity, 0);
  const bundlePriceCentsPreview =
    state.bundlePrice.trim() === "" ? null : parseCents(state.bundlePrice);
  const savingsCents =
    bundlePriceCentsPreview !== null ? regularPriceCents - bundlePriceCentsPreview : null;
  const savingsPercent =
    savingsCents !== null && regularPriceCents > 0
      ? (savingsCents / regularPriceCents) * 100
      : null;
  const totalQuantity = items.reduce((sum, r) => sum + r.quantity, 0);

  const heroImage = items.find((r) => r.menuItem.image_url)?.menuItem.image_url ?? null;

  // How many more units are needed to reach the 2-item combo minimum.
  const itemsNeeded = Math.max(0, 2 - totalQuantity);

  // ── Publish readiness ────────────────────────────────────────────────
  const hasItems = items.length > 0 && totalQuantity >= 2;
  const hasValidPrice =
    bundlePriceCentsPreview !== null &&
    bundlePriceCentsPreview > 0 &&
    savingsCents !== null &&
    savingsCents > 0;
  const hasSchedule = Object.keys(formToSchedule(state.schedule)).length > 0;
  const isReadyToPublish = hasItems && hasValidPrice && hasSchedule;

  const nextRequiredStep =
    items.length === 0
      ? "Add at least one item"
      : totalQuantity < 2
        ? "Total quantity across items must be at least 2"
        : bundlePriceCentsPreview === null || bundlePriceCentsPreview <= 0
          ? "Set a bundle price greater than $0"
          : savingsCents !== null && savingsCents <= 0
            ? "Combo price must be below the combined regular value"
            : !hasSchedule
              ? "Choose at least one active day"
              : null;

  function autoGenerateName(): string {
    const names = items.map((r) => r.menuItem.name);
    if (names.length === 0) return "";
    if (names.length === 1) return `${names[0]} Combo`;
    if (names.length === 2) return `${names[0]} + ${names[1]} Combo`;
    return `${names[0]} + ${names.length - 1} More Combo`;
  }

  function tryClose() {
    if (dirty) {
      setDiscardOpen(true);
    } else {
      onOpenChange(false);
    }
  }

  function handleSave() {
    setHasAttemptedSubmit(true);
    setNameError(null);
    setPriceError(null);
    setItemsError(null);
    setMaxClaimsError(null);
    setScheduleErrors({});
    setScheduleFormError(null);

    const name = state.name.trim();
    if (name.length < 2 || name.length > 100) {
      setNameError("Name must be between 2 and 100 characters.");
      return;
    }

    if (items.length === 0 || totalQuantity < 2) {
      setItemsError(
        "Add at least 2 items total — one item at quantity 2+, or two or more distinct items."
      );
      return;
    }

    const priceCents = Math.round(parseFloat(state.bundlePrice) * 100);
    if (isNaN(priceCents) || priceCents <= 0) {
      setPriceError("Bundle price must be greater than $0.00.");
      return;
    }
    if (priceCents >= regularPriceCents) {
      setPriceError(
        `Bundle price must be less than the combined regular value of ${formatCents(regularPriceCents)}.`
      );
      return;
    }

    let maxClaimsPerCustomerPerDay: number | null = null;
    if (state.maxClaimsPerCustomerPerDay.trim() !== "") {
      const parsed = parseInt(state.maxClaimsPerCustomerPerDay, 10);
      if (isNaN(parsed) || parsed < 1) {
        setMaxClaimsError("Must be at least 1, or blank for unlimited.");
        return;
      }
      maxClaimsPerCustomerPerDay = parsed;
    }

    const rawSchedule = formToSchedule(state.schedule);
    if (Object.keys(rawSchedule).length === 0) {
      setScheduleFormError("Select at least one day.");
      return;
    }
    const schedErrs = validateRawSchedule(rawSchedule);
    if (schedErrs) {
      setScheduleErrors(schedErrs);
      return;
    }

    onSave({
      name,
      bundle_price_cents: priceCents,
      max_claims_per_customer_per_day: maxClaimsPerCustomerPerDay,
      schedule: rawSchedule,
      status: state.status,
      items: items.map((r) => ({ menu_item_id: r.menuItem.id, quantity: r.quantity })),
    });
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o) tryClose();
        }}
      >
        <DialogContent
          showCloseButton={false}
          className="max-w-4xl sm:max-w-4xl p-0 gap-0 overflow-hidden flex flex-col"
        >
          <DialogTitle className="sr-only">{isEditing ? "Edit Combo" : "Create Combo"}</DialogTitle>

          {/* Header */}
          <div className="flex items-center justify-between px-7 py-4 border-b shrink-0">
            <div className="flex items-center gap-3.5">
              <div className="size-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Layers className="size-5 text-primary" />
              </div>
              <div>
                <span className="text-base font-semibold">
                  {isEditing ? "Edit Combo" : "Create Combo Deal"}
                </span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Bundle a fixed set of items at one flat price on a recurring schedule
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDirty(true);
                  dispatch({ type: "reset", item });
                  setItems(initItems(item));
                }}
                disabled={busy}
                className="gap-1.5 text-muted-foreground h-8 px-2.5"
              >
                <RotateCcw className="size-3.5" />
                Reset
              </Button>
              <button
                type="button"
                onClick={tryClose}
                className="rounded-md p-1.5 hover:bg-accent text-muted-foreground transition-colors"
                aria-label="Close"
              >
                <X className="size-4" />
              </button>
            </div>
          </div>

          {/* Body + Footer */}
          <form
            onSubmit={(e) => e.preventDefault()}
            noValidate
            className="flex flex-col flex-1 min-h-0"
          >
            <div className="flex flex-row flex-1 min-h-0 overflow-hidden">
              {/* Left: form */}
              <div className="flex-1 min-w-0 overflow-y-auto px-7 pt-5 pb-5 space-y-5">
                {/* Combo Name */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="bd-name">Combo Name</Label>
                    <button
                      type="button"
                      disabled={busy || items.length === 0}
                      onClick={() => {
                        setDirty(true);
                        dispatch({ type: "set", field: "name", value: autoGenerateName() });
                      }}
                      className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors disabled:opacity-40 disabled:pointer-events-none"
                    >
                      <Sparkles className="size-3" />
                      Auto-generate name
                    </button>
                  </div>
                  <Input
                    id="bd-name"
                    value={state.name}
                    onChange={(e) => {
                      setDirty(true);
                      dispatch({ type: "set", field: "name", value: e.target.value });
                    }}
                    maxLength={100}
                    placeholder="e.g. Burger + Beer"
                    disabled={busy}
                    aria-invalid={!!nameError}
                  />
                  {nameError && <p className="text-xs text-destructive">{nameError}</p>}
                  <p
                    className={`text-xs text-right ${
                      state.name.length > 100 ? "text-destructive" : "text-muted-foreground"
                    }`}
                  >
                    {state.name.length}/100
                  </p>
                </div>

                {/* Items */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label>
                      Items <span className="text-destructive">*</span>
                    </Label>
                    <span className="text-xs text-muted-foreground">
                      {items.length}/{MAX_ITEM_ROWS} distinct
                    </span>
                  </div>

                  {/* Minimum-2 requirement chip — always visible so the rule is obvious up front. */}
                  <div
                    className={cn(
                      "flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium",
                      itemsNeeded === 0
                        ? "border-green-600/20 bg-green-50 text-green-700"
                        : "border-amber-500/20 bg-amber-50 text-amber-700"
                    )}
                  >
                    {itemsNeeded === 0 ? (
                      <>
                        <CheckCircle className="size-3.5 shrink-0" />
                        Minimum reached — a combo needs at least 2 items.
                      </>
                    ) : (
                      <>
                        <AlertCircle className="size-3.5 shrink-0" />
                        {totalQuantity === 0
                          ? "A combo needs at least 2 items. Add 2 or more to continue."
                          : `Add ${itemsNeeded} more item${itemsNeeded === 1 ? "" : "s"} — a combo needs at least 2.`}
                      </>
                    )}
                  </div>

                  {items.length === 0 ? (
                    <div className="border border-dashed rounded-md py-7 px-4 text-center space-y-3">
                      <Package className="size-6 text-muted-foreground/40 mx-auto" />
                      <p className="text-xs text-muted-foreground">No items added yet.</p>
                      <Button
                        type="button"
                        onClick={() => setPickerOpen(true)}
                        disabled={busy}
                        className="gap-1.5"
                      >
                        <Plus className="size-4" />
                        Add Items
                      </Button>
                    </div>
                  ) : (
                    <div className="border rounded-md divide-y divide-border">
                      {items.map((row) => (
                        <div key={row.menuItem.id} className="flex items-center gap-2.5 px-3 py-2">
                          {row.menuItem.image_url ? (
                            <img
                              src={row.menuItem.image_url}
                              alt={row.menuItem.name}
                              className="size-9 rounded-md object-cover shrink-0"
                            />
                          ) : (
                            <div className="size-9 rounded-md bg-muted shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{row.menuItem.name}</p>
                            <p className="text-[10px] text-muted-foreground">
                              {formatCents(row.menuItem.price_cents)} each
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <button
                              type="button"
                              onClick={() => setQuantity(row.menuItem.id, row.quantity - 1)}
                              disabled={busy || row.quantity <= 1}
                              className="size-6 rounded-md border border-border flex items-center justify-center text-muted-foreground hover:bg-muted disabled:opacity-40"
                              aria-label="Decrease quantity"
                            >
                              <Minus className="size-3" />
                            </button>
                            <span className="w-5 text-center text-sm font-medium">
                              {row.quantity}
                            </span>
                            <button
                              type="button"
                              onClick={() => setQuantity(row.menuItem.id, row.quantity + 1)}
                              disabled={busy || row.quantity >= MAX_QUANTITY}
                              className="size-6 rounded-md border border-border flex items-center justify-center text-muted-foreground hover:bg-muted disabled:opacity-40"
                              aria-label="Increase quantity"
                            >
                              <Plus className="size-3" />
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeItem(row.menuItem.id)}
                            disabled={busy}
                            className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
                            aria-label="Remove item"
                          >
                            <X className="size-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {items.length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setPickerOpen(true)}
                      disabled={busy || atRowCap}
                      className="w-full gap-1.5 border-dashed"
                    >
                      <Plus className="size-4" />
                      {atRowCap ? `Maximum ${MAX_ITEM_ROWS} items reached` : "Add another item"}
                    </Button>
                  )}
                  {itemsError && <p className="text-xs text-destructive">{itemsError}</p>}
                </div>

                {/* Bundle price */}
                <div className="border rounded-lg bg-muted-foreground/[0.03] p-4 space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Combined Regular Value</span>
                    <span className="font-semibold">{formatCents(regularPriceCents)}</span>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="bd-price">
                      Bundle Price <span className="text-destructive">*</span>
                    </Label>
                    <div className="relative w-40">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none select-none">
                        $
                      </span>
                      <Input
                        id="bd-price"
                        type="text"
                        inputMode="decimal"
                        placeholder="e.g. 18.00"
                        value={state.bundlePrice}
                        onChange={(e) => {
                          setDirty(true);
                          dispatch({
                            type: "set",
                            field: "bundlePrice",
                            value: e.target.value.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1"),
                          });
                        }}
                        disabled={busy}
                        aria-invalid={!!priceError}
                        fullWidth={false}
                        className="w-full pl-6"
                      />
                    </div>
                    {priceError && <p className="text-xs text-destructive">{priceError}</p>}
                  </div>
                  {/* Quick discount presets — compute a bundle price from the combined value. */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-muted-foreground">Set from savings:</span>
                    {PRESETS.map((preset) => (
                      <button
                        key={preset.label}
                        type="button"
                        disabled={busy || regularPriceCents === 0}
                        onClick={() => {
                          setDirty(true);
                          dispatch({
                            type: "set",
                            field: "bundlePrice",
                            value: (
                              Math.round(regularPriceCents * (1 - preset.percent / 100)) / 100
                            ).toFixed(2),
                          });
                        }}
                        className="text-xs border border-border rounded-md px-2.5 h-7 text-foreground hover:bg-muted transition-colors disabled:opacity-40"
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                  {bundlePriceCentsPreview !== null &&
                    savingsCents !== null &&
                    savingsCents <= 0 && (
                      <p className="text-sm font-medium text-destructive">
                        Combo price must be below the combined regular value (
                        {formatCents(regularPriceCents)})
                      </p>
                    )}
                </div>

                {/* Schedule */}
                <div className="border-y py-5 bg-muted-foreground/[0.07] -mx-7 px-7">
                  <DealScheduleInput
                    schedule={formToSchedule(state.schedule)}
                    onChange={(raw) => {
                      setDirty(true);
                      dispatch({ type: "setSchedule", value: scheduleToForm(raw) });
                      setScheduleErrors({});
                      setScheduleFormError(null);
                    }}
                    error={scheduleFormError ?? Object.values(scheduleErrors)[0]}
                  />
                </div>

                {/* Max claims */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Max claims per customer per day</span>
                    <span className="text-xs text-muted-foreground">Prevents promotion abuse</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setDirty(true);
                        dispatch({ type: "set", field: "maxClaimsPerCustomerPerDay", value: "" });
                        setMaxClaimsError(null);
                      }}
                      disabled={busy}
                      className={cn(
                        "flex items-start gap-3 rounded-lg border p-3 text-left transition-colors",
                        state.maxClaimsPerCustomerPerDay === ""
                          ? "border-primary"
                          : "border-border hover:border-primary/40"
                      )}
                    >
                      <span
                        className={cn(
                          "mt-0.5 size-4 shrink-0 rounded-full border-2 flex items-center justify-center",
                          state.maxClaimsPerCustomerPerDay === ""
                            ? "border-primary"
                            : "border-muted-foreground/40"
                        )}
                      >
                        {state.maxClaimsPerCustomerPerDay === "" && (
                          <span className="size-2 rounded-full bg-primary" />
                        )}
                      </span>
                      <div>
                        <p className="text-sm font-semibold">Unlimited</p>
                        <p className="text-xs text-muted-foreground">No daily order restriction</p>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (state.maxClaimsPerCustomerPerDay === "") {
                          setDirty(true);
                          dispatch({
                            type: "set",
                            field: "maxClaimsPerCustomerPerDay",
                            value: "1",
                          });
                        }
                      }}
                      disabled={busy}
                      className={cn(
                        "flex items-start gap-3 rounded-lg border p-3 text-left transition-colors",
                        state.maxClaimsPerCustomerPerDay !== ""
                          ? "border-primary"
                          : "border-border hover:border-primary/40"
                      )}
                    >
                      <span
                        className={cn(
                          "mt-0.5 size-4 shrink-0 rounded-full border-2 flex items-center justify-center",
                          state.maxClaimsPerCustomerPerDay !== ""
                            ? "border-primary"
                            : "border-muted-foreground/40"
                        )}
                      >
                        {state.maxClaimsPerCustomerPerDay !== "" && (
                          <span className="size-2 rounded-full bg-primary" />
                        )}
                      </span>
                      <div>
                        <p className="text-sm font-semibold">Limit Per User</p>
                        <p className="text-xs text-muted-foreground">Fixed number of redemptions</p>
                      </div>
                    </button>
                  </div>
                  {state.maxClaimsPerCustomerPerDay !== "" && (
                    <div className="flex items-center gap-3">
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={state.maxClaimsPerCustomerPerDay}
                        onChange={(e) => {
                          setDirty(true);
                          dispatch({
                            type: "set",
                            field: "maxClaimsPerCustomerPerDay",
                            value: e.target.value.replace(/\D/g, ""),
                          });
                        }}
                        disabled={busy}
                        aria-invalid={!!maxClaimsError}
                        fullWidth={false}
                        className="w-20"
                      />
                      <span className="text-sm text-muted-foreground">
                        redemption(s) per guest per 24 hours
                      </span>
                    </div>
                  )}
                  {maxClaimsError && <p className="text-xs text-destructive">{maxClaimsError}</p>}
                </div>

                {/* Status (edit only) */}
                {isEditing && (
                  <div className="space-y-1.5">
                    <Label>Status</Label>
                    <SegmentedToggle
                      value={state.status}
                      onChange={(s) => {
                        setDirty(true);
                        dispatch({ type: "setStatus", value: s });
                      }}
                      options={[
                        { value: "active", label: "Active" },
                        { value: "inactive", label: "Inactive" },
                      ]}
                    />
                  </div>
                )}

                {apiError && <p className="text-sm text-destructive">{apiError}</p>}
              </div>

              {/* Right: live preview */}
              <div className="w-[360px] shrink-0 border-l overflow-y-auto bg-slate-100">
                <div className="px-5 py-5 space-y-4">
                  <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                    <span className="size-2 rounded-full bg-green-500 shrink-0" />
                    Live Customer Preview
                  </div>

                  {items.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-slate-300 h-52 flex items-center justify-center">
                      <p className="text-sm text-slate-400 text-center px-4">
                        Add items to see a live preview
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Combo card */}
                      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
                        <div className="relative aspect-video bg-slate-200">
                          {heroImage ? (
                            <img src={heroImage} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center">
                              <Layers className="size-10 text-slate-400" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                          <div className="absolute top-2.5 left-2.5 flex items-center gap-1 bg-black/50 text-white px-2 py-0.5 rounded-md text-[10px] font-semibold backdrop-blur-sm">
                            <Layers className="size-3 fill-current" />
                            COMBO DEAL
                          </div>
                          {savingsCents !== null && savingsCents > 0 && savingsPercent !== null && (
                            <div className="absolute bottom-2.5 right-2.5 bg-primary text-primary-foreground text-sm font-bold px-2.5 py-1 rounded-md">
                              Save {Math.floor(savingsPercent)}%
                            </div>
                          )}
                        </div>

                        <div className="p-4 space-y-3">
                          <p className="text-sm font-semibold leading-snug text-slate-900 line-clamp-2">
                            {state.name.trim() || autoGenerateName() || "Combo Deal"}
                          </p>
                          <div className="space-y-1.5">
                            {items.map((row) => (
                              <div
                                key={row.menuItem.id}
                                className="flex items-center gap-2 text-xs"
                              >
                                <span className="shrink-0 font-medium text-slate-400 w-6">
                                  {row.quantity}×
                                </span>
                                <span className="flex-1 min-w-0 text-slate-700 truncate">
                                  {row.menuItem.name}
                                </span>
                                <span className="shrink-0 text-slate-400">
                                  {formatCents(row.menuItem.price_cents * row.quantity)}
                                </span>
                              </div>
                            ))}
                          </div>

                          <div className="border-t border-slate-100 pt-3 flex items-center justify-between gap-3">
                            <div>
                              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                                Bundle Price
                              </p>
                              <div className="flex items-baseline gap-1.5 mt-0.5">
                                <span className="text-xl font-bold text-slate-900">
                                  {bundlePriceCentsPreview !== null && bundlePriceCentsPreview > 0
                                    ? formatCents(bundlePriceCentsPreview)
                                    : "—"}
                                </span>
                                {savingsCents !== null && savingsCents > 0 && (
                                  <span className="text-sm text-slate-400 line-through">
                                    {formatCents(regularPriceCents)}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="shrink-0 bg-slate-900 text-white text-xs font-semibold px-4 py-2 rounded-lg">
                              Add Combo
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Deal Economics */}
                      {hasValidPrice && (
                        <div className="border border-slate-200 rounded-xl p-4 space-y-3 bg-white shadow-sm">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                              <LayoutGrid className="size-3.5 text-slate-400" />
                              Deal Economics Breakdown
                            </div>
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                              Per Order
                            </span>
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div className="rounded-lg bg-slate-50 p-2">
                              <p className="text-[10px] text-slate-400 mb-1">Full Value</p>
                              <p className="text-sm font-semibold text-slate-800">
                                {formatCents(regularPriceCents)}
                              </p>
                            </div>
                            <div className="rounded-lg bg-red-50 p-2">
                              <p className="text-[10px] text-red-500 mb-1">Merchant Cost</p>
                              <p className="text-sm font-semibold text-red-500">
                                -{formatCents(savingsCents!)}
                              </p>
                            </div>
                            <div className="rounded-lg bg-green-50 p-2">
                              <p className="text-[10px] text-green-600 mb-1">Revenue</p>
                              <p className="text-sm font-semibold text-green-600">
                                {formatCents(bundlePriceCentsPreview!)}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t px-7 py-3.5 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-1.5 text-xs">
                {isReadyToPublish ? (
                  <>
                    <CheckCircle className="size-3.5 text-green-600 shrink-0" />
                    <span className="text-muted-foreground">
                      Validation passed • Ready to publish
                    </span>
                  </>
                ) : hasAttemptedSubmit ? (
                  <div className="flex items-center gap-1.5 bg-destructive/10 text-destructive border border-destructive/20 rounded-md px-2.5 py-1">
                    <AlertCircle className="size-3.5 shrink-0" />
                    <span className="font-medium">{nextRequiredStep}</span>
                  </div>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                {isEditing ? (
                  <>
                    <Button type="button" variant="outline" onClick={tryClose} disabled={busy}>
                      Cancel
                    </Button>
                    <Button type="button" onClick={handleSave} disabled={busy}>
                      {busy ? (
                        <Spinner className="size-4 border-primary-foreground" />
                      ) : (
                        "Save Changes"
                      )}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button type="button" variant="outline" onClick={tryClose} disabled={busy}>
                      Discard
                    </Button>
                    <Button type="button" onClick={handleSave} disabled={busy} className="gap-1.5">
                      {busy ? (
                        <Spinner className="size-4 border-primary-foreground" />
                      ) : (
                        <>
                          <span>Publish Combo Deal</span>
                          <ArrowRight className="size-4" />
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ItemPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        user={user}
        storeId={storeId}
        itemRows={items}
        maxRows={MAX_ITEM_ROWS}
        onAdd={addItem}
        onRemove={removeItem}
        onSetQuantity={setQuantity}
      />

      <DiscardDialog
        open={discardOpen}
        onConfirm={() => {
          setDiscardOpen(false);
          onOpenChange(false);
        }}
        onCancel={() => setDiscardOpen(false)}
      />
    </>
  );
}

// ─── Item Picker Dialog (results from server-side search, not a pre-loaded list) ──

function ItemPickerDialog({
  open,
  onOpenChange,
  user,
  storeId,
  itemRows,
  maxRows,
  onAdd,
  onRemove,
  onSetQuantity,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: ReturnType<typeof useAdminAuth>;
  storeId: string;
  itemRows: BundleItemRow[];
  maxRows: number;
  onAdd: (item: MenuItem) => void;
  onRemove: (id: string) => void;
  onSetQuantity: (id: string, quantity: number) => void;
}) {
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setSearchInput("");
    setSearchQuery("");
    const t = setTimeout(() => inputRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [open]);

  const { data: searchResults, isFetching: searchLoading } = useQuery({
    queryKey: ["menu-items", storeId, "bundle-deal-search", searchQuery],
    queryFn: () => listMenuItems(user!, storeId, { search: searchQuery, limit: 20 }),
    enabled: !!user && open && searchQuery.trim() !== "",
  });

  const rowsById = new Map(itemRows.map((r) => [r.menuItem.id, r]));
  const distinctCount = itemRows.length;
  const totalQuantity = itemRows.reduce((sum, r) => sum + r.quantity, 0);
  const itemsNeeded = Math.max(0, 2 - totalQuantity);
  const atMax = distinctCount >= maxRows;

  const results = searchResults?.data ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="max-w-lg sm:max-w-lg p-0 gap-0 overflow-hidden flex flex-col"
      >
        <DialogTitle className="sr-only">Add items to combo</DialogTitle>

        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b shrink-0">
          <div className="min-w-0">
            <span className="text-base font-semibold">Add Items</span>
            <p className="text-xs text-muted-foreground mt-0.5">
              {distinctCount === 0
                ? "Pick at least 2 items to build the combo"
                : `${distinctCount}/${maxRows} distinct • ${totalQuantity} total`}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-md p-1.5 hover:bg-accent text-muted-foreground transition-colors shrink-0"
            aria-label="Close"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 pt-4 pb-3 shrink-0">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
              <Input
                ref={inputRef}
                placeholder="Search items..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && setSearchQuery(searchInput)}
                className="pl-8"
              />
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setSearchQuery(searchInput)}
            >
              Search
            </Button>
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-2">
          {searchQuery.trim() === "" ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Search menu items to add them to the combo.
            </p>
          ) : searchLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="size-5 border-primary" />
            </div>
          ) : results.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No items match your search.
            </p>
          ) : (
            <div className="border rounded-md divide-y divide-border">
              {results.map((item) => {
                const row = rowsById.get(item.id);
                const added = row !== undefined;
                return (
                  <div key={item.id} className="flex items-center gap-3 px-3 py-2">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="size-9 rounded-md object-cover shrink-0"
                      />
                    ) : (
                      <div className="size-9 rounded-md bg-muted shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.name}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {formatCents(item.price_cents)}
                      </p>
                    </div>
                    {added ? (
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          type="button"
                          onClick={() => onSetQuantity(item.id, row!.quantity - 1)}
                          disabled={row!.quantity <= 1}
                          className="size-6 rounded-md border border-border flex items-center justify-center text-muted-foreground hover:bg-muted disabled:opacity-40"
                          aria-label="Decrease quantity"
                        >
                          <Minus className="size-3" />
                        </button>
                        <span className="w-5 text-center text-sm font-medium">{row!.quantity}</span>
                        <button
                          type="button"
                          onClick={() => onSetQuantity(item.id, row!.quantity + 1)}
                          disabled={row!.quantity >= MAX_QUANTITY}
                          className="size-6 rounded-md border border-border flex items-center justify-center text-muted-foreground hover:bg-muted disabled:opacity-40"
                          aria-label="Increase quantity"
                        >
                          <Plus className="size-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => onRemove(item.id)}
                          className="ml-1 text-muted-foreground hover:text-destructive transition-colors"
                          aria-label="Remove item"
                        >
                          <X className="size-3.5" />
                        </button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => onAdd(item)}
                        disabled={atMax}
                        className="gap-1 h-8 shrink-0"
                      >
                        <Plus className="size-3.5" />
                        Add
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {atMax && (
            <p className="text-xs text-muted-foreground text-center pt-3">
              Maximum {maxRows} distinct items reached. Remove one to add another.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="border-t px-5 py-3.5 flex items-center justify-between gap-3 shrink-0">
          <span
            className={cn(
              "flex items-center gap-1.5 text-xs font-medium",
              itemsNeeded === 0 ? "text-green-700" : "text-amber-700"
            )}
          >
            {itemsNeeded === 0 ? (
              <>
                <CheckCircle className="size-3.5 shrink-0" />
                Minimum reached
              </>
            ) : (
              <>
                <AlertCircle className="size-3.5 shrink-0" />
                {totalQuantity === 0 ? "Add at least 2 items" : `Add ${itemsNeeded} more`}
              </>
            )}
          </span>
          <Button type="button" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
